import React, { useMemo, useState, useEffect, useRef } from "react";
import { InterViewSlotService } from "@/services/InterViewSlot";
import Loader from "@/components/Loader";
import useToastStore from "@/components/Common/Toast/toastStore";
import {
  Check,
  X,
  Mail,
  Clock,
  ChevronDown,
  Phone,
  Search,
} from "lucide-react";
import Pagination from "@/components/Common/Pagination";
import usePaginationStore from "@/hooks/stores/usePaginationStore";
import useAuth from "@/hooks/stores/useAuth";
import ContactRequestChatDialog, {
  resolveChatReceiverUserId,
} from "@/components/contactRequests/ContactRequestChatDialog";

const ContactRequests = () => {
  const { userID } = useAuth();
  const { showToast } = useToastStore;
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const { getPerPage, setPerPage } = usePaginationStore();
  const [itemsPerPage, setItemsPerPage] = useState(() =>
    getPerPage("contact-requests-table"),
  );
  const [currentPage, setCurrentPage] = useState(1);

  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState({
    candidateName: "",
    candidateEmail: "",
    candidatePhone: "",
    content: "",
    vacancyTitle: "",
    companyName: "",
    campaignName: "",
    vacancyStage: "",
  });

  const [activeContactRequestId, setActiveContactRequestId] = useState(null);
  const [activeContactRequest, setActiveContactRequest] = useState(null);
  const [chatFallbackNote, setChatFallbackNote] = useState("");

  const statuses = [
    {
      value: "received",
      label: "Received",
      icon: <Clock size={16} className="text-blue-500" />,
      bgColor: "bg-blue-100",
      textColor: "text-blue-700",
    },
    {
      value: "contacted",
      label: "Contacted",
      icon: <Mail size={16} className="text-amber-500" />,
      bgColor: "bg-amber-100",
      textColor: "text-amber-700",
    },
    {
      value: "resolved",
      label: "Resolved",
      icon: <Check size={16} className="text-emerald-500" />,
      bgColor: "bg-emerald-100",
      textColor: "text-emerald-700",
    },
    {
      value: "unresolved",
      label: "Unresolved",
      icon: <X size={16} className="text-rose-500" />,
      bgColor: "bg-rose-100",
      textColor: "text-rose-700",
    },
  ];

  useEffect(() => {
    fetchContactRequests();

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 2000);
    return () => clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, itemsPerPage]);

  const filteredRequests = useMemo(() => {
    const q = debouncedSearchQuery.trim().toLowerCase();
    if (!q) return requests;

    const matches = (value) =>
      String(value ?? "")
        .toLowerCase()
        .includes(q);

    return requests.filter((request) => {
      const candidateName = request.candidate_name || request.candidate?.name;
      const email = request.email || request.candidate?.email;
      const companyName = request.vacancy?.company?.name;
      const vacancyTitle = request.vacancy?.title;
      const status = request.status;
      const phone = request.candidate?.phone_number;
      const message = request.message || request.note;

      return (
        matches(candidateName) ||
        matches(email) ||
        matches(companyName) ||
        matches(vacancyTitle) ||
        matches(status) ||
        matches(phone) ||
        matches(message)
      );
    });
  }, [requests, debouncedSearchQuery]);

  const totalPages = useMemo(() => {
    const per = Number(itemsPerPage) || 1;
    return Math.max(1, Math.ceil(filteredRequests.length / per));
  }, [filteredRequests.length, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const paginatedRequests = useMemo(() => {
    const per = Number(itemsPerPage) || 1;
    const start = (currentPage - 1) * per;
    return filteredRequests.slice(start, start + per);
  }, [filteredRequests, currentPage, itemsPerPage]);

  const handlePerPageChange = (option) => {
    const next = option?.value ?? option;
    setItemsPerPage(next);
    setPerPage("contact-requests-table", next);
    setCurrentPage(1);
  };

  const fetchContactRequests = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await InterViewSlotService.getContactRequests();
      setRequests(Array.isArray(response) ? response : response.data || []);
    } catch (err) {
      console.error("Error fetching contact requests:", err);
      const errorMessage = err.message || "Failed to fetch contact requests";
      setError(errorMessage);
      showToast({ message: errorMessage, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      await InterViewSlotService.updateContactRequestStatus(
        requestId,
        newStatus,
      );
      setRequests((prev) =>
        prev.map((req) =>
          req.id === requestId ? { ...req, status: newStatus } : req,
        ),
      );
      setOpenDropdown(null);
      showToast({ message: `Status updated to ${newStatus}`, type: "success" });
    } catch (err) {
      showToast({
        message: err.message || "Failed to update status",
        type: "error",
      });
    }
  };

  const closeMessageDialog = () => {
    setShowMessageDialog(false);
    setActiveContactRequestId(null);
    setActiveContactRequest(null);
    setChatFallbackNote("");
  };

  const handleShowMessage = (request) => {
    const message = request.message || request.note || "";

    setSelectedMessage({
      candidateName:
        request.candidate_name ||
        request.candidate?.name ||
        request.name ||
        "-",
      candidateEmail: request.email || request.candidate?.email || "-",
      candidatePhone: request.candidate?.phone_number || "-",
      content: message || "-",
      vacancyTitle: request.vacancy?.title || "-",
      companyName: request.vacancy?.company?.name || "-",
      campaignName:
        request.vacancy?.campaign?.name || request.vacancy?.campaign_id || "-",
      vacancyStage:
        request.vacancy_status?.status?.name || request.status || "-",
    });

    setChatFallbackNote(message);
    setActiveContactRequest(request);
    setActiveContactRequestId(request.id);
    setShowMessageDialog(true);
  };

  const getStatusStyle = (statusValue) => {
    const found =
      statuses.find((s) => s.value === statusValue.toLowerCase()) ||
      statuses[0];
    return `${found.bgColor} ${found.textColor}`;
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12 px-8 mx-auto flex flex-col gap-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                Contact Requests
              </h1>
              <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                Manage candidate requests for interview scheduling and track
                communication status.
              </p>
            </div>
          </div>
          <div className="w-full md:w-[384px]">
            <div className="self-stretch h-11 px-3.5 py-2.5 bg-background rounded-lg border border-input inline-flex justify-start items-center gap-2 overflow-hidden">
              <Search size={18} className="text-muted-foreground" />
              <input
                id="contact-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search"
                className="flex-1 bg-transparent border-none outline-none text-muted-foreground text-base font-normal leading-6"
                autoComplete="off"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-100 text-red-700 rounded-md border border-red-200">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-card shadow rounded-lg  border border-border">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted border-b border-border">
              <tr>
                <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Candidate
                </th>
                <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Message
                </th>
                <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Requested At
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <Loader bgColor="transparent" />
                  </td>
                </tr>
              ) : filteredRequests.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-muted-foreground"
                  >
                    No contact requests found
                    {debouncedSearchQuery.trim() ? " for your search." : "."}
                  </td>
                </tr>
              ) : (
                paginatedRequests.map((request) => (
                  <tr
                    key={request.id}
                    className="hover:bg-muted transition-colors duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                      {request.candidate_name || request.candidate?.name || "-"}
                      <br />
                      <span className="text-xs text-primary-dark font-normal">
                        {request.email || request.candidate?.email || "-"}
                      </span>
                    </td>
                    <td
                      className="px-6 py-4 text-sm text-muted-foreground max-w-xs truncate"
                      title={request.vacancy?.company?.name}
                    >
                      {request.vacancy?.company?.name || "-"}
                      <br />
                      <span className="text-xs text-primary-dark">
                        {request.vacancy?.title || "-"}
                      </span>
                    </td>
                    <td
                      className="px-6 py-4 text-sm text-muted-foreground max-w-xs truncate cursor-pointer hover:bg-muted/80 transition-colors"
                      title="Open chat"
                      onClick={() => handleShowMessage(request)}
                    >
                      {request.message || request.note || "-"}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm relative">
                      <button
                        onClick={() =>
                          setOpenDropdown(
                            openDropdown === request.id ? null : request.id,
                          )
                        }
                        className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize transition-all border border-transparent hover:border-border ${getStatusStyle(request.status)}`}
                      >
                        {request.status}
                        <ChevronDown size={14} className="opacity-60" />
                      </button>

                      {openDropdown === request.id && (
                        <div
                          ref={dropdownRef}
                          className="absolute left-0 mt-2 w-48 bg-card rounded-lg shadow-xl border border-border z-50 py-1 overflow-hidden animate-in fade-in zoom-in duration-100"
                        >
                          {statuses.map((s) => (
                            <button
                              key={s.value}
                              onClick={() =>
                                handleStatusUpdate(request.id, s.value)
                              }
                              className={`flex items-center gap-3 w-full px-4 py-2.5 text-sm text-left hover:bg-muted transition-colors ${request.status === s.value ? "bg-muted font-medium" : ""}`}
                            >
                              <span
                                className={`flex items-center justify-center w-6 h-6 rounded-full ${s.bgColor}`}
                              >
                                {s.icon}
                              </span>
                              <span className="text-foreground">{s.label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td
                      className="px-6 py-4 text-sm text-muted-foreground max-w-xs truncate"
                      title={request.candidate?.phone_number}
                    >
                      {request.candidate?.phone_number || "-"}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {request.created_at
                        ? new Date(request.created_at).toLocaleString()
                        : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <ContactRequestChatDialog
          isOpen={showMessageDialog}
          onClose={closeMessageDialog}
          userID={userID}
          contactRequestId={activeContactRequestId}
          contactRequestRecord={activeContactRequest}
          initialReceiverUserId={
            activeContactRequest
              ? resolveChatReceiverUserId(activeContactRequest, userID)
              : null
          }
          chatFallbackNote={chatFallbackNote}
          headerTitle={`Message from ${selectedMessage.candidateName}`}
          headerEmail={selectedMessage.candidateEmail}
          headerPhone={selectedMessage.candidatePhone}
          jobContext={{
            vacancyTitle: selectedMessage.vacancyTitle,
            companyName: selectedMessage.companyName,
            campaignName: selectedMessage.campaignName,
            vacancyStage: selectedMessage.vacancyStage,
          }}
        />
      </div>

      <div className="w-[90%] mx-auto p-6">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          per_page={Number(itemsPerPage) || 1}
          onPerPageChange={handlePerPageChange}
        />
      </div>
    </div>
  );
};

export default ContactRequests;
